import { useEffect, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@web/components/ui/button";
import type { ReaderWorkspace } from "../../../reader/reader-workspace";
import { ReaderOverlayPanel } from "./reader-overlay-panel";

interface ReaderCatalogPanelProps {
  workspace: ReaderWorkspace;
  activeChapterId?: string;
  onClose: () => void;
  onContentRefresh: () => void;
  onChapterSelect: (chapterId: string) => void;
}

export function ReaderCatalogPanel({
  workspace,
  activeChapterId,
  onClose,
  onContentRefresh,
  onChapterSelect,
}: ReaderCatalogPanelProps) {
  const activeChapterRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // 打开目录时把当前章节滚到面板中间
    const frameId = requestAnimationFrame(() => {
      activeChapterRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
    });

    return () => cancelAnimationFrame(frameId);
  }, [activeChapterId]);

  if (!workspace.opened) return null;
  const chapters = workspace.opened.chapters;
  const refreshChapterId = activeChapterId ?? workspace.opened.chapter.chapterId;
  const cachedChapterIds = new Set([
    workspace.opened.chapter.chapterId,
    ...(workspace.opened.cachedChapters ?? []).map(chapter => chapter.chapterId),
    ...workspace.openedChapters.map(chapter => chapter.chapterId),
  ]);

  return (
    <ReaderOverlayPanel
      title="目录"
      titleAccessory={
        <span className="rounded-full border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {chapters.length} 章
        </span>
      }
      action={
        <div className="flex items-center gap-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="刷新目录"
            title="刷新目录"
            onClick={() => {
              // 重新打开当前章节以刷新目录快照
              void workspace.handleOpenChapterFromDirectory(refreshChapterId, { forceRemote: true });
            }}
          >
            <RefreshCw className="size-4" />
            目录
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="刷新正文"
            title="刷新正文"
            onClick={() => {
              // 按当前阅读位置重新获取正文内容
              void onContentRefresh();
            }}
          >
            <RefreshCw className="size-4 text-primary" />
            正文
          </Button>
        </div>
      }
      onClose={onClose}
    >
      <div className="grid gap-1">
        {chapters.map(chapter => {
          const cached = cachedChapterIds.has(chapter.id);

          return (
            <Button
              key={chapter.id}
              type="button"
              ref={chapter.id === activeChapterId ? activeChapterRef : undefined}
              variant={chapter.id === activeChapterId ? "secondary" : "ghost"}
              className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_1rem] justify-normal gap-2 overflow-hidden whitespace-normal"
              onClick={() => {
                onClose();
                void onChapterSelect(chapter.id);
              }}
            >
              <span className="min-w-0 truncate text-left" title={chapter.title}>{chapter.title}</span>
              <span className="flex w-4 shrink-0 justify-center">
                {cached && (
                  <span
                    className="size-2 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.14)]"
                    aria-label="已缓存"
                    title="已缓存"
                  />
                )}
              </span>
            </Button>
          );
        })}
      </div>
    </ReaderOverlayPanel>
  );
}
